import { Routes, Route, Navigate } from 'react-router-dom'
import { isCustomSiteHost } from '@reptree/shared'
import { Suspense, type ReactNode } from 'react'
import { lazyRetry } from './lib/lazy'
import { useAuth } from './lib/auth'
import { LoadingPane } from './components/ui'
import { MfaGate } from './components/MfaGate'
import { ToastDockProvider } from './components/toast/ToastDock'
import Landing from './pages/Landing'
import Login from './pages/Login'

// Code-split everything behind the public entry so the landing + login load lean.
// lazyRetry (not bare lazy): a mid-session deploy prunes old chunks — recover by reloading once.
const AppShell = lazyRetry(() => import('./components/AppShell').then((m) => ({ default: m.AppShell })))
const Onboarding = lazyRetry(() => import('./pages/Onboarding'))
const Dashboard = lazyRetry(() => import('./pages/Dashboard'))
const Clients = lazyRetry(() => import('./pages/Clients'))
const ClientDetail = lazyRetry(() => import('./pages/ClientDetail'))
const Contacts = lazyRetry(() => import('./pages/Contacts'))
const Board = lazyRetry(() => import('./pages/Board'))
const Settings = lazyRetry(() => import('./pages/Settings'))
const Customize = lazyRetry(() => import('./pages/Customize'))
const Website = lazyRetry(() => import('./pages/Website'))
const Leads = lazyRetry(() => import('./pages/Leads'))
const PublicSite = lazyRetry(() => import('./pages/PublicSite'))
const Team = lazyRetry(() => import('./pages/Team'))
const Admin = lazyRetry(() => import('./pages/Admin'))
const Support = lazyRetry(() => import('./pages/Support'))
const Help = lazyRetry(() => import('./pages/Help'))
const Packages = lazyRetry(() => import('./pages/Packages'))
const Analytics = lazyRetry(() => import('./pages/Analytics'))
const Bookkeeping = lazyRetry(() => import('./pages/Bookkeeping'))
const Sales = lazyRetry(() => import('./pages/sales/Sales'))
const Billing = lazyRetry(() => import('./pages/Billing'))
const BookkeepingClosed = lazyRetry(() => import('./pages/BookkeepingClosed'))
const CalendarPage = lazyRetry(() => import('./pages/CalendarPage'))
const Documents = lazyRetry(() => import('./pages/Documents'))
const Videos = lazyRetry(() => import('./pages/Videos'))
const Credentials = lazyRetry(() => import('./pages/Credentials'))
const PublicPackage = lazyRetry(() => import('./pages/PublicPackage'))
const PublicSubmit = lazyRetry(() => import('./pages/PublicSubmit'))
const PublicDoc = lazyRetry(() => import('./pages/PublicDoc'))
const Legal = lazyRetry(() => import('./pages/Legal'))
const PortalHome = lazyRetry(() => import('./pages/PortalHome'))
const AgencyDashboard = lazyRetry(() => import('./pages/AgencyDashboard'))
const PortalSettings = lazyRetry(() => import('./pages/PortalSettings'))
const PortalForms = lazyRetry(() => import('./pages/PortalForms'))
const PortalCredentials = lazyRetry(() => import('./pages/PortalCredentials'))
const PortalDocumentsHub = lazyRetry(() => import('./pages/PortalDocumentsHub'))
const PortalVideosHub = lazyRetry(() => import('./pages/PortalVideosHub'))
const PortalAudition = lazyRetry(() => import('./pages/PortalAudition'))
const PortalHistory = lazyRetry(() => import('./pages/PortalHistory'))
const PortalMyInfo = lazyRetry(() => import('./pages/PortalMyInfo'))
const PortalSharing = lazyRetry(() => import('./pages/PortalSharing'))
const PortalTodo = lazyRetry(() => import('./pages/PortalTodo'))
const ResetPassword = lazyRetry(() => import('./pages/ResetPassword'))
const AuthConfirm = lazyRetry(() => import('./pages/AuthConfirm'))
const PortalLayout = lazyRetry(() => import('./components/PortalLayout').then((m) => ({ default: m.PortalLayout })))
const IntakeForms = lazyRetry(() => import('./pages/IntakeForms'))
const IntakeRun = lazyRetry(() => import('./pages/IntakeRun'))
const BookingSheet = lazyRetry(() => import('./pages/BookingSheet'))
const Blog = lazyRetry(() => import('./pages/Blog'))
const BlogPost = lazyRetry(() => import('./pages/BlogPost'))
const Family = lazyRetry(() => import('./pages/Family'))
const ClaimAccount = lazyRetry(() => import('./pages/ClaimAccount'))

// Late-reveal: fast loads resolve inside the delay, so gates never flash a spinner.
function FullScreenLoader() {
  return (
    <div className="grid min-h-full place-items-center">
      <LoadingPane className="" />
    </div>
  )
}

/** Renders the two-factor check instead of any authed surface until the session is aal2. */
function MfaBoundary({ children }: { children: ReactNode }) {
  const { session, mfaRequired } = useAuth()
  if (session && mfaRequired) return <MfaGate />
  return <>{children}</>
}

function Protected({ children }: { children: ReactNode }) {
  const { session, loading, me, mfaRequired } = useAuth()
  if (loading) return <FullScreenLoader />
  if (!session) return <Navigate to="/login" replace />
  if (mfaRequired) return <MfaGate />
  if (me && me.memberships.length === 0) {
    return me.clients.length > 0 || me.has_history ? <Navigate to="/portal" replace /> : <Navigate to="/onboarding" replace />
  }
  return <>{children}</>
}

/** Auth gate for the talent portal — renders the portal chrome; child routes fill it. */
function PortalGate() {
  const { session, loading, me, mfaRequired } = useAuth()
  if (loading) return <FullScreenLoader />
  if (!session) return <Navigate to="/login" replace />
  if (mfaRequired) return <MfaGate />
  if (me && me.memberships.length > 0 && me.clients.length === 0 && !me.has_history) return <Navigate to="/app" replace />
  return <PortalLayout />
}

/** A custom domain (CNAME) pointing at the app → serve that agency's published site, not the marketing site.
 *  The first-party host list is shared with the server (its CSP decision), so the two never disagree. */
function isCustomHost(): boolean {
  if (typeof window === 'undefined') return false
  return isCustomSiteHost(window.location.hostname)
}

export default function App() {
  const customHost = isCustomHost()
  // The toast dock (upload progress, the new-version notice) sits outside the routes: mounted once, never torn down.
  return (
    <ToastDockProvider>
    <Suspense fallback={<FullScreenLoader />}>
    <Routes>
      <Route path="/" element={customHost ? <PublicSite byDomain /> : <Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<MfaBoundary><ResetPassword /></MfaBoundary>} />
      <Route path="/auth/confirm" element={<AuthConfirm />} />
      <Route path="/onboarding" element={<MfaBoundary><Onboarding /></MfaBoundary>} />
      <Route path="/p/:token" element={<PublicPackage />} />
      <Route path="/s/:token" element={<PublicSubmit />} />
      <Route path="/d/:token" element={<PublicDoc />} />
      <Route path="/terms" element={<Legal doc="terms" />} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/site/:slug" element={<PublicSite />} />
      <Route path="/site/:slug/:pageSlug" element={<PublicSite />} />
      <Route path="/booking/:id" element={<Protected><BookingSheet /></Protected>} />
      <Route path="/i/:token" element={<IntakeRun />} />
      <Route path="/claim/:token" element={<ClaimAccount />} />
      <Route path="/family" element={<MfaBoundary><Family /></MfaBoundary>} />
      <Route path="/portal" element={<PortalGate />}>
        <Route index element={<PortalHome />} />
        <Route path="agency/:cid" element={<AgencyDashboard />} />
        <Route path="forms" element={<PortalForms />} />
        <Route path="credentials" element={<PortalCredentials />} />
        <Route path="documents" element={<PortalDocumentsHub />} />
        <Route path="videos" element={<PortalVideosHub />} />
        <Route path="settings" element={<PortalSettings />} />
        <Route path="a/:cid/:oppId" element={<PortalAudition />} />
        <Route path="history/:source/:id" element={<PortalHistory />} />
        {/* One profile, Phase 3 (TALENT_CONSENT_UI): each page sends the talent home while the switch is off. */}
        <Route path="me/info" element={<PortalMyInfo />} />
        <Route path="me/sharing" element={<PortalSharing />} />
        <Route path="me/todo" element={<PortalTodo />} />
      </Route>
      <Route
        path="/app"
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="board" element={<Board />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="team" element={<Team />} />
        <Route path="packages" element={<Packages />} />
        <Route path="intake" element={<IntakeForms />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="documents" element={<Documents />} />
        <Route path="videos" element={<Videos />} />
        <Route path="credentials" element={<Credentials />} />
      <Route path="analytics" element={<Analytics />} />
        <Route path="bookkeeping" element={<Bookkeeping />} />
        <Route path="bookkeeping/closed" element={<BookkeepingClosed />} />
        {/* One route, tabbed internally with `?tab=` (the Admin console idiom) rather than a nested layout:
            every Sales tab reads the same funnel list, so a shared parent that fetched it once beats six
            sibling routes each re-asking on arrival. */}
        <Route path="sales" element={<Sales />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
        <Route path="customize" element={<Customize />} />
        <Route path="website" element={<Website />} />
        <Route path="leads" element={<Leads />} />
        <Route path="admin" element={<Admin />} />
        <Route path="support" element={<Support />} />
        <Route path="help" element={<Help />} />
        <Route path="help/:slug" element={<Help />} />
      </Route>
      <Route path="*" element={customHost ? <PublicSite byDomain /> : <Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ToastDockProvider>
  )
}
